
import {
  divWithStyle,
  linkClass,
  SiteUrlsContext,
  vizPortalFontStyle
} from '@tableau/data-catalog-shared';
import { assertNever } from '@tableau/data-structures';
import { DialogPositioningBehavior, DialogType, DialogWidget, TabStyles } from '@tableau/widgets';
import * as React from 'react';
import { Query } from 'react-apollo';
import { Link } from 'react-router-dom';

import { Messages } from '../../../gen-src/Localize';
import {
  FlowFieldDetailsQuery,
  FlowFieldDetailsQuery_flowOutputSteps_outputFields,
  FlowFieldDetailsQueryVariables,
} from '../../../gen-src/queries/FlowFieldDetailsQuery';
import { FlowFieldDetailsQueryGql } from '../../queries/FlowFieldDetailsQueryGql';
import { renderQueryResult } from '../renderQueryResult';

const dialogWidth = 500;

const DetailsPane = divWithStyle('DetailsPane', {
  ...vizPortalFontStyle,
  fontSize: TabStyles.Typography.Sizing.Default,
});

export const DetailsName = divWithStyle('DetailsName', {
  fontSize: TabStyles.Typography.Sizing.XLarge,
});

const DetailsProperties = divWithStyle('DetailsProperties', {
  display: 'table',
  paddingTop: TabStyles.Spacing.DefaultSpace,
});

const DetailsProperty = divWithStyle('DetailsProperty', {
  display: 'table-row',
});

const DetailsLabel = divWithStyle('DetailsLabel', {
  display: 'table-cell',
  padding: TabStyles.Spacing.DefaultSpace,
  color: TabStyles.Typography.Color.Secondary,
  textAlign: 'right',
});

const DetailsValue = divWithStyle('DetailsValue', {
  display: 'table-cell',
  padding: TabStyles.Spacing.DefaultSpace,
});

const DetailsPlaceholderMessage = divWithStyle('DetailsPlaceholderMessage', {
  paddingTop: TabStyles.Spacing.DefaultSpace,
});

export interface FlowFieldDetailsDialogProps {
  readonly flowOutputStepId: string;
  readonly flowFieldId: string;
  readonly onClose: () => void;
}

export class FlowFieldDetailsDialog extends React.Component<FlowFieldDetailsDialogProps> {

  public render(): JSX.Element {
    return (
      <Query<FlowFieldDetailsQuery, FlowFieldDetailsQueryVariables>
        query={FlowFieldDetailsQueryGql}
        variables={{ flowOutputStepId: this.props.flowOutputStepId, flowFieldId: this.props.flowFieldId }}
      >
        {result => renderQueryResult(result, { renderData: this.renderFlowFieldDetailsDialog })}
      </Query>
    );
  }

  private readonly renderFlowFieldDetailsDialog = (data: FlowFieldDetailsQuery): React.ReactNode => {
    const flowOutputStep = data.flowOutputSteps && data.flowOutputSteps[0];
    if (!flowOutputStep) {
      return null;
    }
    const flowField = flowOutputStep.outputFields && flowOutputStep.outputFields[0];
    if (!flowField) {
      return null;
    }

    return (
      <DialogWidget
        testId='flow-field-details'
        title={Messages.FieldDetails()}
        type={DialogType.NormalWithTitleBar}
        positioningBehavior={DialogPositioningBehavior.Centered}
        dialogWidth={dialogWidth}
        showCloseButton={true}
        showFooter={false}
        isModal={true}
        onCloseRequest={this.props.onClose}
        handleGlassClick={this.props.onClose}
      >
        <DetailsPane>
          <DetailsName>{flowField.name}</DetailsName>
          {this.renderProperties(flowField)}
        </DetailsPane>
      </DialogWidget>
    );
  }

  private renderProperties(flowField: FlowFieldDetailsQuery_flowOutputSteps_outputFields): React.ReactNode {
    switch (flowField.__typename) {
      case 'FlowColumnOutputField':
        if (flowField.column) {
          if (flowField.column.name !== null) {
            const column = flowField.column;
            const table = column.table;
            // The table for a column should always be defined.
            const tableId = table ? table.id : '';
            const columnName = column.name !== null ? column.name : Messages.PermissionsRequired();
            return (
              <DetailsProperties>
                <DetailsProperty>
                  <DetailsLabel>{Messages.FlowFieldDetails_OutputsToColumn()}</DetailsLabel>
                  <DetailsValue>
                    <SiteUrlsContext.Consumer>
                      {urls =>
                        <Link
                          className={linkClass}
                          to={urls.catalogTableColumnPath(tableId, column.id)}
                          title={columnName}
                        >
                          {columnName}
                        </Link>
                      }
                    </SiteUrlsContext.Consumer>
                  </DetailsValue>
                </DetailsProperty>
              </DetailsProperties>
            );
          } else {
            return (
              <DetailsPlaceholderMessage>
                {Messages.PermissionsRequired()}
              </DetailsPlaceholderMessage>
            );
          }
        } else {
          return (
            <DetailsPlaceholderMessage>
              {Messages.FlowFieldDetails_OutputColumnUnavailable()}
            </DetailsPlaceholderMessage>
          );
        }
      case 'FlowFieldOutputField':
        if (flowField.field) {
          if (flowField.field.name !== null) {
            const field = flowField.field;
            const datasource = field.datasource;
            // The datasource for a flow output field should always be defined,
            // if the field edge is, and it should always be a published data source,
            // since flows can't output workbooks.
            const datasourceVizportalUrlId =
              datasource && datasource.__typename === 'PublishedDatasource' ? datasource.vizportalUrlId : '';
            const fieldName = field.name !== null ? field.name : Messages.PermissionsRequired();
            return (
              <DetailsProperties>
                <DetailsProperty>
                  <DetailsLabel>{Messages.FlowFieldDetails_OutputsToField()}</DetailsLabel>
                  <DetailsValue>
                    <SiteUrlsContext.Consumer>
                      {urls =>
                        <Link
                          className={linkClass}
                          to={urls.datasourceLineageFieldPath(datasourceVizportalUrlId, field.id)}
                          title={fieldName}
                        >
                          {fieldName}
                        </Link>
                      }
                    </SiteUrlsContext.Consumer>
                  </DetailsValue>
                </DetailsProperty>
              </DetailsProperties>
            );
          } else {
            return (
              <DetailsPlaceholderMessage>
                {Messages.PermissionsRequired()}
              </DetailsPlaceholderMessage>
            );
          }
        } else {
          return (
            <DetailsPlaceholderMessage>
              {Messages.FlowFieldDetails_OutputFieldUnavailable()}
            </DetailsPlaceholderMessage>
          );
        }
      default:
        assertNever(flowField);
        return null;
    }
  }

}
